"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Download, Filter, MapPin, AlertTriangle, FileText, MapIcon } from "lucide-react"
import { Link } from "react-router-dom"
import bgImage from "@/assets/favicon.png"

interface LocationData {
  id: string
  area: string
  district: string
  coordinates: { lat: number; lng: number }
  totalIncidents: number
  caseTypes: {
    theft: number
    assault: number
    fraud: number
    domestic: number
    others: number
  }
  riskLevel: "HIGH" | "MEDIUM" | "LOW"
  nearbyStations: string[]
  recentIncidents: {
    date: string
    gdNumber: string
    caseType: string
  }[]
}

// Dummy data for location heatmap
const locationData: LocationData[] = [
  {
    id: "1",
    area: "Bandra West",
    district: "Western Suburbs",
    coordinates: { lat: 19.0596, lng: 72.8295 },
    totalIncidents: 78,
    caseTypes: {
      theft: 32,
      assault: 15,
      fraud: 10,
      domestic: 12,
      others: 9,
    },
    riskLevel: "HIGH",
    nearbyStations: ["Bandra Police Station", "Khar Police Station"],
    recentIncidents: [
      {
        date: "2024-01-26",
        gdNumber: "GD/2024/126",
        caseType: "Theft",
      },
      {
        date: "2024-01-24",
        gdNumber: "GD/2024/118",
        caseType: "Assault",
      },
      {
        date: "2024-01-22",
        gdNumber: "GD/2024/112",
        caseType: "Theft",
      },
    ],
  },
  {
    id: "2",
    area: "Andheri East",
    district: "Western Suburbs",
    coordinates: { lat: 19.1136, lng: 72.8697 },
    totalIncidents: 65,
    caseTypes: {
      theft: 28,
      assault: 12,
      fraud: 8,
      domestic: 10,
      others: 7,
    },
    riskLevel: "HIGH",
    nearbyStations: ["Andheri Police Station", "MIDC Police Station"],
    recentIncidents: [
      {
        date: "2024-01-25",
        gdNumber: "GD/2024/122",
        caseType: "Theft",
      },
      {
        date: "2024-01-23",
        gdNumber: "GD/2024/115",
        caseType: "Fraud",
      },
      {
        date: "2024-01-20",
        gdNumber: "GD/2024/105",
        caseType: "Domestic Violence",
      },
    ],
  },
  {
    id: "3",
    area: "Borivali",
    district: "Western Suburbs",
    coordinates: { lat: 19.2362, lng: 72.8546 },
    totalIncidents: 52,
    caseTypes: {
      theft: 22,
      assault: 10,
      fraud: 7,
      domestic: 8,
      others: 5,
    },
    riskLevel: "MEDIUM",
    nearbyStations: ["Borivali Police Station", "Kandivali Police Station"],
    recentIncidents: [
      {
        date: "2024-01-24",
        gdNumber: "GD/2024/119",
        caseType: "Theft",
      },
      {
        date: "2024-01-21",
        gdNumber: "GD/2024/108",
        caseType: "Assault",
      },
    ],
  },
  {
    id: "4",
    area: "Malad",
    district: "Western Suburbs",
    coordinates: { lat: 19.1823, lng: 72.8482 },
    totalIncidents: 48,
    caseTypes: {
      theft: 20,
      assault: 9,
      fraud: 6,
      domestic: 8,
      others: 5,
    },
    riskLevel: "MEDIUM",
    nearbyStations: ["Malad Police Station", "Malvani Police Station"],
    recentIncidents: [
      {
        date: "2024-01-23",
        gdNumber: "GD/2024/116",
        caseType: "Domestic Violence",
      },
      {
        date: "2024-01-19",
        gdNumber: "GD/2024/102",
        caseType: "Theft",
      },
    ],
  },
  {
    id: "5",
    area: "Dadar",
    district: "Central Mumbai",
    coordinates: { lat: 19.0178, lng: 72.8478 },
    totalIncidents: 42,
    caseTypes: {
      theft: 18,
      assault: 8,
      fraud: 5,
      domestic: 7,
      others: 4,
    },
    riskLevel: "MEDIUM",
    nearbyStations: ["Dadar Police Station", "Matunga Police Station"],
    recentIncidents: [
      {
        date: "2024-01-22",
        gdNumber: "GD/2024/113",
        caseType: "Theft",
      },
      {
        date: "2024-01-18",
        gdNumber: "GD/2024/098",
        caseType: "Assault",
      },
    ],
  },
  {
    id: "6",
    area: "Colaba",
    district: "South Mumbai",
    coordinates: { lat: 18.9067, lng: 72.8147 },
    totalIncidents: 35,
    caseTypes: {
      theft: 15,
      assault: 7,
      fraud: 4,
      domestic: 5,
      others: 4,
    },
    riskLevel: "MEDIUM",
    nearbyStations: ["Colaba Police Station", "Cuffe Parade Police Station"],
    recentIncidents: [
      {
        date: "2024-01-21",
        gdNumber: "GD/2024/109",
        caseType: "Theft",
      },
      {
        date: "2024-01-17",
        gdNumber: "GD/2024/094",
        caseType: "Fraud",
      },
    ],
  },
  {
    id: "7",
    area: "Powai",
    district: "Central Suburbs",
    coordinates: { lat: 19.1176, lng: 72.906 },
    totalIncidents: 38,
    caseTypes: {
      theft: 16,
      assault: 8,
      fraud: 5,
      domestic: 6,
      others: 3,
    },
    riskLevel: "MEDIUM",
    nearbyStations: ["Powai Police Station", "Vikhroli Police Station"],
    recentIncidents: [
      {
        date: "2024-01-20",
        gdNumber: "GD/2024/106",
        caseType: "Theft",
      },
      {
        date: "2024-01-16",
        gdNumber: "GD/2024/090",
        caseType: "Domestic Violence",
      },
    ],
  },
  {
    id: "8",
    area: "Chembur",
    district: "Eastern Suburbs",
    coordinates: { lat: 19.0522, lng: 72.9005 },
    totalIncidents: 32,
    caseTypes: {
      theft: 14,
      assault: 6,
      fraud: 4,
      domestic: 5,
      others: 3,
    },
    riskLevel: "LOW",
    nearbyStations: ["Chembur Police Station", "Tilak Nagar Police Station"],
    recentIncidents: [
      {
        date: "2024-01-19",
        gdNumber: "GD/2024/103",
        caseType: "Assault",
      },
      {
        date: "2024-01-15",
        gdNumber: "GD/2024/086",
        caseType: "Theft",
      },
    ],
  },
  {
    id: "9",
    area: "Worli",
    district: "South Mumbai",
    coordinates: { lat: 19.0096, lng: 72.8175 },
    totalIncidents: 30,
    caseTypes: {
      theft: 12,
      assault: 6,
      fraud: 4,
      domestic: 5,
      others: 3,
    },
    riskLevel: "LOW",
    nearbyStations: ["Worli Police Station", "N.M. Joshi Marg Police Station"],
    recentIncidents: [
      {
        date: "2024-01-18",
        gdNumber: "GD/2024/099",
        caseType: "Fraud",
      },
      {
        date: "2024-01-14",
        gdNumber: "GD/2024/082",
        caseType: "Theft",
      },
    ],
  },
  {
    id: "10",
    area: "Goregaon",
    district: "Western Suburbs",
    coordinates: { lat: 19.1663, lng: 72.8526 },
    totalIncidents: 36,
    caseTypes: {
      theft: 15,
      assault: 7,
      fraud: 5,
      domestic: 6,
      others: 3,
    },
    riskLevel: "MEDIUM",
    nearbyStations: ["Goregaon Police Station", "Dindoshi Police Station"],
    recentIncidents: [
      {
        date: "2024-01-17",
        gdNumber: "GD/2024/095",
        caseType: "Theft",
      },
      {
        date: "2024-01-13",
        gdNumber: "GD/2024/078",
        caseType: "Assault",
      },
    ],
  },
]

// Dummy data for hotspot analysis
const hotspotData = [
  {
    id: "1",
    area: "Bandra West",
    location: "Hill Road Shopping Area",
    caseType: "Theft",
    incidents: 18,
    timePattern: "Evening (17:00-21:00)",
    targetPattern: "Mobile phones, wallets",
    riskLevel: "HIGH",
  },
  {
    id: "2",
    area: "Andheri East",
    location: "MIDC Industrial Area",
    caseType: "Theft",
    incidents: 15,
    timePattern: "Night (22:00-02:00)",
    targetPattern: "Industrial equipment, vehicles",
    riskLevel: "HIGH",
  },
  {
    id: "3",
    area: "Dadar",
    location: "Dadar Railway Station",
    caseType: "Theft",
    incidents: 12,
    timePattern: "Rush hours (08:00-10:00, 18:00-20:00)",
    targetPattern: "Mobile phones, bags",
    riskLevel: "MEDIUM",
  },
  {
    id: "4",
    area: "Malad",
    location: "Malad West Market",
    caseType: "Theft",
    incidents: 10,
    timePattern: "Afternoon (13:00-17:00)",
    targetPattern: "Purses, jewelry",
    riskLevel: "MEDIUM",
  },
  {
    id: "5",
    area: "Bandra East",
    location: "BKC Business District",
    caseType: "Fraud",
    incidents: 8,
    timePattern: "Business hours (10:00-18:00)",
    targetPattern: "Corporate employees",
    riskLevel: "MEDIUM",
  },
]

export default function LocationHeatmapReport() {
  const [filters, setFilters] = useState({
    district: "",
    caseType: "",
    riskLevel: "",
    dateRange: "month",
  })
  const [searchTerm, setSearchTerm] = useState("")

  const districts = ["All", "Western Suburbs", "Eastern Suburbs", "Central Mumbai", "South Mumbai", "Central Suburbs"]
  const caseTypes = ["All", "Theft", "Assault", "Fraud", "Domestic Violence", "Others"]
  const riskLevels = ["All", "HIGH", "MEDIUM", "LOW"]
  const dateRanges = [
    { value: "week", label: "Last 7 Days" },
    { value: "month", label: "Last 30 Days" },
    { value: "quarter", label: "Last 90 Days" },
    { value: "year", label: "Last 365 Days" },
  ]

  const filteredLocations = locationData.filter((location) => {
    if (filters.district && filters.district !== "All" && location.district !== filters.district) {
      return false
    }
    if (filters.riskLevel && filters.riskLevel !== "All" && location.riskLevel !== filters.riskLevel) {
      return false
    }
    if (searchTerm && !location.area.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false
    }
    return true
  })

  const getRiskLevelBadge = (riskLevel: string) => {
    const riskConfig = {
      HIGH: { color: "bg-red-100 text-red-800", text: "High Risk" },
      MEDIUM: { color: "bg-yellow-100 text-yellow-800", text: "Medium Risk" },
      LOW: { color: "bg-green-100 text-green-800", text: "Low Risk" },
    }
    const config = riskConfig[riskLevel as keyof typeof riskConfig]
    return <Badge className={`${config.color} hover:${config.color}`}>{config.text}</Badge>
  }

  const exportToCSV = () => {
    const headers = [
      "Area",
      "District",
      "Total Incidents",
      "Theft",
      "Assault",
      "Fraud",
      "Domestic Violence",
      "Others",
      "Risk Level",
      "Nearby Stations",
    ]
    const csvContent = [
      headers.join(","),
      ...filteredLocations.map((location) =>
        [
          location.area,
          location.district,
          location.totalIncidents,
          location.caseTypes.theft,
          location.caseTypes.assault,
          location.caseTypes.fraud,
          location.caseTypes.domestic,
          location.caseTypes.others,
          location.riskLevel,
          location.nearbyStations.join("; "),
        ].join(","),
      ),
    ].join("\n")

    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `location-heatmap-${new Date().toISOString().split("T")[0]}.csv`
    a.click()
  }

  // Calculate statistics
  const highRiskAreas = filteredLocations.filter((location) => location.riskLevel === "HIGH").length
  const mediumRiskAreas = filteredLocations.filter((location) => location.riskLevel === "MEDIUM").length
//   const lowRiskAreas = filteredLocations.filter((location) => location.riskLevel === "LOW").length
  const totalIncidents = filteredLocations.reduce((sum, location) => sum + location.totalIncidents, 0)

  return (
    <div className="min-h-screen bg-gradient-to-r from-[#0d2846]/3 via-[#e2af58]/10 to-[#0d2846]/3 p-6">
      <div
        className="absolute inset-0 bg-no-repeat bg-center bg-contain opacity-3 pointer-events-none"
        style={{
          backgroundImage: `url(${bgImage})`,
        }}
      />
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <Link to="/reports" className="text-blue-600 hover:text-blue-800 text-sm mb-2 inline-block">
              ← Back to Reports
            </Link>
            <h1 className="text-3xl font-bold text-black">GD Location Heatmap Report</h1>
            <p className="text-gray-600 mt-1">Visualize incident density by location with hotspot analysis</p>
          </div>
          <Button onClick={exportToCSV} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <MapPin className="h-5 w-5 text-gray-600" />
                <div>
                  <div className="text-2xl font-bold text-black">{filteredLocations.length}</div>
                  <p className="text-sm text-gray-600">Areas Analyzed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <div>
                  <div className="text-2xl font-bold text-red-600">{highRiskAreas}</div>
                  <p className="text-sm text-gray-600">High Risk Areas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
                <div>
                  <div className="text-2xl font-bold text-yellow-600">{mediumRiskAreas}</div>
                  <p className="text-sm text-gray-600">Medium Risk Areas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-blue-600" />
                <div>
                  <div className="text-2xl font-bold text-blue-600">{totalIncidents}</div>
                  <p className="text-sm text-gray-600">Total Incidents</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Map Placeholder */}
        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg text-black flex items-center gap-2">
              <MapIcon className="h-5 w-5" />
              Interactive Heatmap
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative w-full h-[500px] bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
              <div className="text-center p-6">
                <MapIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">Interactive Map Visualization</h3>
                <p className="text-gray-500 max-w-md mx-auto mb-4">
                  This area would display an interactive map with heatmap overlay showing incident density across
                  different locations. The map would be color-coded based on incident frequency and risk levels.
                </p>
                <Button className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">Load Interactive Map</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg text-black flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters & Search
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">District</Label>
                <Select value={filters.district} onValueChange={(value) => setFilters({ ...filters, district: value })}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="Select district" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    {districts.map((district) => (
                      <SelectItem key={district} value={district}>
                        {district}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Case Type</Label>
                <Select value={filters.caseType} onValueChange={(value) => setFilters({ ...filters, caseType: value })}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="Select case type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    {caseTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Risk Level</Label>
                <Select
                  value={filters.riskLevel}
                  onValueChange={(value) => setFilters({ ...filters, riskLevel: value })}
                >
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="Select risk level" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    {riskLevels.map((level) => (
                      <SelectItem key={level} value={level}>
                        {level}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Date Range</Label>
                <Select
                  value={filters.dateRange}
                  onValueChange={(value) => setFilters({ ...filters, dateRange: value })}
                >
                  <SelectTrigger className="border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    {dateRanges.map((range) => (
                      <SelectItem key={range.value} value={range.value}>
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
                <Input
                  placeholder="Search by area name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-gray-300"
                />
              </div>
              <Button className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">Apply Filters</Button>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="areas" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 bg-gray-100">
            <TabsTrigger value="areas" className="data-[state=active]:bg-white">
              Area Analysis
            </TabsTrigger>
            <TabsTrigger value="hotspots" className="data-[state=active]:bg-white">
              Hotspot Analysis
            </TabsTrigger>
            <TabsTrigger value="recommendations" className="data-[state=active]:bg-white">
              Recommendations
            </TabsTrigger>
          </TabsList>

          {/* Area Analysis Tab */}
          <TabsContent value="areas">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black">Area-wise Incident Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-black font-semibold">Area</TableHead>
                        <TableHead className="text-black font-semibold">District</TableHead>
                        <TableHead className="text-black font-semibold">Total Incidents</TableHead>
                        <TableHead className="text-black font-semibold">Theft</TableHead>
                        <TableHead className="text-black font-semibold">Assault</TableHead>
                        <TableHead className="text-black font-semibold">Fraud</TableHead>
                        <TableHead className="text-black font-semibold">Domestic</TableHead>
                        <TableHead className="text-black font-semibold">Risk Level</TableHead>
                        <TableHead className="text-black font-semibold">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredLocations
                        .sort((a, b) => b.totalIncidents - a.totalIncidents)
                        .map((location) => (
                          <TableRow key={location.id} className="hover:bg-gray-50">
                            <TableCell className="font-medium text-black">{location.area}</TableCell>
                            <TableCell className="text-gray-600">{location.district}</TableCell>
                            <TableCell className="font-bold text-black">{location.totalIncidents}</TableCell>
                            <TableCell className="text-gray-600">{location.caseTypes.theft}</TableCell>
                            <TableCell className="text-gray-600">{location.caseTypes.assault}</TableCell>
                            <TableCell className="text-gray-600">{location.caseTypes.fraud}</TableCell>
                            <TableCell className="text-gray-600">{location.caseTypes.domestic}</TableCell>
                            <TableCell>{getRiskLevelBadge(location.riskLevel)}</TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm" className="hover:bg-gray-100">
                                View Details
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Hotspot Analysis Tab */}
          <TabsContent value="hotspots">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  Crime Hotspot Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    Hotspots are specific locations within areas that show a high concentration of particular crime
                    types. These locations often require targeted interventions and increased patrol presence.
                  </p>
                </div>

                <div className="space-y-6">
                  {hotspotData.map((hotspot) => (
                    <div
                      key={hotspot.id}
                      className={`p-4 border rounded-lg ${
                        hotspot.riskLevel === "HIGH"
                          ? "border-red-200 bg-red-50"
                          : hotspot.riskLevel === "MEDIUM"
                            ? "border-yellow-200 bg-yellow-50"
                            : "border-green-200 bg-green-50"
                      }`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge
                              className={
                                hotspot.riskLevel === "HIGH"
                                  ? "bg-red-100 text-red-800"
                                  : hotspot.riskLevel === "MEDIUM"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-green-100 text-green-800"
                              }
                            >
                              {hotspot.riskLevel} RISK
                            </Badge>
                            <Badge
                              className={
                                hotspot.caseType === "Theft"
                                  ? "bg-blue-100 text-blue-800"
                                  : hotspot.caseType === "Assault"
                                    ? "bg-red-100 text-red-800"
                                    : hotspot.caseType === "Fraud"
                                      ? "bg-orange-100 text-orange-800"
                                      : "bg-purple-100 text-purple-800"
                              }
                            >
                              {hotspot.caseType}
                            </Badge>
                          </div>
                          <h3 className="text-lg font-semibold text-black">
                            {hotspot.location} <span className="text-gray-600">({hotspot.area})</span>
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2 text-sm">
                            <div>
                              <span className="font-medium text-gray-700">Incidents:</span>{" "}
                              <span className="text-gray-600">{hotspot.incidents}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Time Pattern:</span>{" "}
                              <span className="text-gray-600">{hotspot.timePattern}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Target Pattern:</span>{" "}
                              <span className="text-gray-600">{hotspot.targetPattern}</span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <Button className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">View on Map</Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recommendations Tab */}
          <TabsContent value="recommendations">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black">Patrol & Prevention Recommendations</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="p-4 border border-red-200 bg-red-50 rounded-lg">
                    <h3 className="text-lg font-semibold text-red-800 mb-2">High Risk Areas</h3>
                    <ul className="space-y-4">
                      <li className="flex gap-4">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-200 flex items-center justify-center text-red-800 font-bold">
                          1
                        </div>
                        <div>
                          <h4 className="font-medium text-red-800">Bandra West - Hill Road Shopping Area</h4>
                          <p className="text-red-700 mt-1">
                            Increase patrol presence during evening hours (17:00-21:00). Focus on pickpocketing and
                            mobile theft prevention. Consider plainclothes officers in crowded shopping areas.
                          </p>
                        </div>
                      </li>
                      <li className="flex gap-4">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-200 flex items-center justify-center text-red-800 font-bold">
                          2
                        </div>
                        <div>
                          <h4 className="font-medium text-red-800">Andheri East - MIDC Industrial Area</h4>
                          <p className="text-red-700 mt-1">
                            Implement night patrols (22:00-02:00) and coordinate with private security. Install
                            additional CCTV cameras at key entry/exit points. Engage with business owners for improved
                            security measures.
                          </p>
                        </div>
                      </li>
                    </ul>
                  </div>

                  <div className="p-4 border border-yellow-200 bg-yellow-50 rounded-lg">
                    <h3 className="text-lg font-semibold text-yellow-800 mb-2">Medium Risk Areas</h3>
                    <ul className="space-y-4">
                      <li className="flex gap-4">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-yellow-200 flex items-center justify-center text-yellow-800 font-bold">
                          1
                        </div>
                        <div>
                          <h4 className="font-medium text-yellow-800">Dadar - Railway Station Area</h4>
                          <p className="text-yellow-700 mt-1">
                            Focus on rush hour patrols (08:00-10:00, 18:00-20:00). Coordinate with railway police for
                            joint operations. Install awareness posters about pickpocketing and bag snatching.
                          </p>
                        </div>
                      </li>
                      <li className="flex gap-4">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-yellow-200 flex items-center justify-center text-yellow-800 font-bold">
                          2
                        </div>
                        <div>
                          <h4 className="font-medium text-yellow-800">Malad West Market</h4>
                          <p className="text-yellow-700 mt-1">
                            Implement afternoon patrols (13:00-17:00) with focus on female officers for better
                            engagement with women shoppers. Conduct awareness campaigns with local shop owners.
                          </p>
                        </div>
                      </li>
                    </ul>
                  </div>

                  <div className="p-4 border border-blue-200 bg-blue-50 rounded-lg">
                    <h3 className="text-lg font-semibold text-blue-800 mb-2">General Recommendations</h3>
                    <ul className="space-y-2 list-disc pl-5 text-blue-700">
                      <li>
                        Implement data-driven patrol scheduling based on hotspot analysis and time-of-day patterns
                      </li>
                      <li>
                        Conduct regular community engagement programs in high-risk areas to build trust and gather
                        intelligence
                      </li>
                      <li>
                        Coordinate with municipal authorities for improved street lighting in areas with night-time
                        incidents
                      </li>
                      <li>Deploy mobile CCTV units in temporary hotspots during festivals and special events</li>
                      <li>
                        Establish regular communication channels with resident welfare associations and market
                        associations in high-risk areas
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
